import {StructuredAttachment} from './messenger.models';

/**
 * A FILE ON A MESSAGE (ITP-E-194-10), however it came: read out of today's attachment HTML, or a B-19 structured
 * attachment. What a file card or an image preview draws.
 */
export interface MessageFile {
  name: string;
  /** Bytes, or null when the attachment does not say: today's HTML carries a size only as a `data-size`. */
  size: number | null;
  /** The MIME type when known, else ''. The card keys its mark off the name's extension, not this. */
  type: string;
  /** Where the file downloads from, or null when the attachment names none. */
  url: string | null;
  /** A preview image, when the attachment carries one (B-19). */
  thumbnail?: string | null;
  /**
   * B-19 (ITP-E-207-07): a chat file's id. Such a file names no link: it is opened by asking the server for one
   * (`MessengerCardsPort.downloadLink`) at the moment it is opened.
   */
  attachmentId?: string | null;
  /**
   * ITP-E-249-01 (B-25): a voice message's length in milliseconds. A file with one is a voice message: drawn as a voice
   * bubble, never as an image, a video or a file row.
   */
  voiceDurationMs?: number | null;
  /**
   * ITP-E-249-04 (B-25): a voice message's waveform, 48 peaks from 0 to 100, which its bubble draws as bars; null when it has none,
   * and the bars are flat.
   */
  voicePeaks?: number[] | null;
  /**
   * ITP-E-249-02 (B-25): a voice message still on its way, the sender's Sending bubble: the clip's object URL here, played with
   * nothing fetched. A file the server answered never has one.
   */
  localUrl?: string | null;
}

/** The mark of a file kind: carbon's monochrome `fa-solid` glyph over the format word (`skills/chat-panel.md`). */
export interface FileMark {
  icon: string;
  format: string;
}

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];

const VIDEO_EXTENSIONS = ['mp4', 'm4v', 'mov', 'webm'];

const EXTENSION_ICON: Readonly<Record<string, string>> = {
  pdf: 'fa-solid fa-file-pdf',
  xls: 'fa-solid fa-file-excel', xlsx: 'fa-solid fa-file-excel', xlsm: 'fa-solid fa-file-excel',
  csv: 'fa-solid fa-file-csv',
  doc: 'fa-solid fa-file-word', docx: 'fa-solid fa-file-word',
  ppt: 'fa-solid fa-file-powerpoint', pptx: 'fa-solid fa-file-powerpoint',
  xml: 'fa-solid fa-file-code', json: 'fa-solid fa-file-code', xsd: 'fa-solid fa-file-code', html: 'fa-solid fa-file-code',
  zip: 'fa-solid fa-file-zipper', rar: 'fa-solid fa-file-zipper', '7z': 'fa-solid fa-file-zipper',
  txt: 'fa-solid fa-file-lines', md: 'fa-solid fa-file-lines',
  mp4: 'fa-solid fa-file-video', mov: 'fa-solid fa-file-video', webm: 'fa-solid fa-file-video',
  mp3: 'fa-solid fa-file-audio', wav: 'fa-solid fa-file-audio', m4a: 'fa-solid fa-file-audio',
  msg: 'fa-solid fa-envelope', eml: 'fa-solid fa-envelope'
};

export function extensionOf(name: string): string {
  const dot = (name || '').lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** ITP-E-249-01 (B-25): whether the file is a voice message — one carrying its length. */
export function isVoiceFile(file: MessageFile): boolean {
  return Number.isFinite(file?.voiceDurationMs) && file.voiceDurationMs > 0;
}

/** Whether the file is drawn as a picture: by its type when known, else by its extension. Never a voice message. */
export function isImageFile(file: MessageFile): boolean {
  return !isVoiceFile(file)
    && ((file.type || '').toLowerCase().startsWith('image/') || IMAGE_EXTENSIONS.includes(extensionOf(file.name)));
}

export function fileMarkOf(file: MessageFile): FileMark {
  const extension = extensionOf(file.name);
  const icon = isImageFile(file) ? 'fa-solid fa-file-image' : EXTENSION_ICON[extension] || 'fa-solid fa-file';
  return {icon, format: (extension || 'file').toUpperCase().slice(0, 4)};
}

/** A size as carbon's `formatBytes` writes it: `640 B`, `212 KB`, `1.8 MB`. */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined || !isFinite(bytes) || bytes < 0) {
    return '';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * TODAY'S ATTACHMENT, READ. The composer writes a message's files as `<a href download="name">name</a><br>`, one per
 * file (`ChatEditorComponent.getAttachment`). This reads them back into files: the link, the name, and the size where
 * the anchor carries one (`data-size`). Anything else in the HTML (text, another element, a link without a name) and
 * the whole attachment is not read, so the thread falls back to rendering it as it always has. Parsed inertly: a
 * `DOMParser` document runs no script and loads nothing.
 */
export function filesOfAttachmentHtml(html: string): MessageFile[] | null {
  if (!html || !html.trim() || typeof DOMParser === 'undefined') {
    return null;
  }
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  const files: MessageFile[] = [];
  for (const node of Array.from(body.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent.trim()) {
        return null;
      }
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      continue;
    }
    const element = node as Element;
    if (element.tagName === 'BR') {
      continue;
    }
    if (element.tagName !== 'A') {
      return null;
    }
    const url = (element.getAttribute('href') || '').trim();
    const name = (element.getAttribute('download') || element.textContent || '').trim();
    if (!url || !name) {
      return null;
    }
    const size = Number(element.getAttribute('data-size'));
    files.push({name, url, type: element.getAttribute('type') || '', size: element.hasAttribute('data-size') && isFinite(size) ? size : null});
  }
  return files.length ? files : null;
}

/**
 * A B-19 structured attachment as a file. It names no link: a chat file (one with an id) is opened through a link minted
 * when it is opened (ITP-E-207-07).
 */
export function fileOfStructuredAttachment(attachment: StructuredAttachment): MessageFile {
  return {
    name: attachment.name,
    size: Number.isFinite(attachment.size) ? attachment.size : null,
    type: attachment.type || '',
    url: null,
    thumbnail: attachment.thumbnail || null,
    attachmentId: attachment.id || null,
    voiceDurationMs: Number.isFinite(attachment.voiceDurationMs) && attachment.voiceDurationMs > 0 ? attachment.voiceDurationMs : null,
    // ITP-E-249-04: its waveform, as stored; the bubble draws flat bars for anything that is not 48 peaks.
    voicePeaks: Array.isArray(attachment.voicePeaks) ? attachment.voicePeaks.map(Number) : null
  };
}

/**
 * Whether the file plays as a video: by its type when known, else by its extension. Never a voice message (ITP-E-249-01),
 * whatever its container says: a voice clip plays in its own bubble.
 */
export function isVideoFile(file: MessageFile): boolean {
  return !isVoiceFile(file)
    && ((file.type || '').toLowerCase().startsWith('video/') || VIDEO_EXTENSIONS.includes(extensionOf(file.name)));
}

/** Whether the file is a PDF, which the browser shows rather than downloads. */
export function isPdfFile(file: MessageFile): boolean {
  return (file.type || '').toLowerCase() === 'application/pdf' || extensionOf(file.name) === 'pdf';
}
